import { find, isArray, isEmpty } from "lodash";
import { isSameDay } from "date-fns";
import formatDate from "../../../helpers/formatDate";
import { proposalTimes, type ProposalFields } from "../proposalTimes";
import type {
  Answer,
  ParticipationQuestionnaire,
  Question,
  QuestionnaireProposal,
} from "../types";

// A radio group gives a string, several checkboxes an array, a lone checkbox its value or false
export type AnswerFields = {
  value: string;
  option_ids: string[] | string | false | null;
  custom_option: string;
  // what the participant typed in the other languages, like a survey's custom option
  custom_option_translations?: Record<string, Record<string, string>>;
  custom_proposal: ProposalFields;
};

export function optionIds(value: AnswerFields["option_ids"]): string[] {
  if (isArray(value)) return value;
  return value ? [value] : [];
}

export function defaultAnswers(
  questionnaire: ParticipationQuestionnaire,
): AnswerFields[] {
  return questionnaire.questions.map((question) => {
    const answer = find(questionnaire.my_submission?.answers, {
      question_id: question.id,
    });
    const ids = answer?.option_ids || [];

    return {
      value: answer?.value || "",
      option_ids: question.multiple_select ? ids : ids[0] || "",
      custom_option: "",
      custom_option_translations: {},
      custom_proposal: { date: null, time: null, time_end: null },
    };
  });
}

export function toAnswers(
  questions: Question[],
  fields: AnswerFields[],
): Answer[] {
  return questions.map((question, index) => {
    const field = fields[index];

    if (question.type === "text") {
      return { question_id: question.id, value: field?.value?.trim() || "" };
    }

    const custom =
      question.type === "poll" ? field?.custom_option?.trim() || "" : "";
    const own = field?.custom_proposal;
    // a day without a start goes as such, the server reports it instead of storing midnight
    const ownDate =
      question.type === "schedule" && question.option_adding && own?.date
        ? own.time
          ? proposalTimes(own)
          : { starts_at: null, ends_at: null }
        : null;
    return {
      question_id: question.id,
      // an own answer or date replaces the choice of a single-select question
      option_ids:
        (!isEmpty(custom) || ownDate) && !question.multiple_select
          ? []
          : optionIds(field?.option_ids),
      ...(!isEmpty(custom)
        ? {
            custom_option: custom,
            ...(isEmpty(field?.custom_option_translations)
              ? {}
              : {
                  custom_option_translations: field.custom_option_translations,
                }),
          }
        : {}),
      ...(ownDate ? { custom_proposal: ownDate } : {}),
    };
  });
}

export function proposalLabel(proposal: QuestionnaireProposal): string {
  if (proposal.improper) return I18n.t("js.activitystreams.improper_schedule");
  if (!proposal.starts_at) return "";

  const start = new Date(proposal.starts_at);
  const startText = formatDate(start, "EEEEEE, P, p");
  if (!proposal.ends_at) return startText;

  const end = new Date(proposal.ends_at);
  return `${startText} – ${formatDate(end, isSameDay(start, end) ? "p" : "EEEEEE, P, p")}`;
}
